import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../config';
import {
  etichettaStato, etichettaMotivo, coloreStato, etichettaIdentita,
  numeroMascherato, promuovibileRiga, titoloStato, riarmabileRiga, giaVista,
  eCerved, confrontoPiva, gruppiCerved, coordinateCerved, serveSceltaNumero, fissiDaScegliere,
} from '../pages/campagneData';

/* Il dettaglio di una riga di campagna, in tre blocchi e in quest'ordine: cosa ha dato
   TIM, cosa ha trovato l'indagine, il verdetto.

   Dentro il secondo blocco la SCHEDA DI MAPS viene per prima, e in evidenza: davanti a una
   riga rifiutata la domanda che arriva per prima e' "ma chi ha guardato?", e la scheda su
   cui il motore e' finito e' la risposta — non un dettaglio da mettere in fondo.

   Il dialogo rilegge sempre la riga dal backend (/campagne/riga/{id}) invece di ricevere
   quella gia' in tabella: fra il caricamento della lista e l'apertura del dettaglio il
   lavoro in sottofondo puo' averla cambiata, e il bottino dell'indagine (fissi, cellulari,
   email, scheda Maps) e' proprio la parte che nasce li'. */

const fmtData = (at) => {
  if (!at) return '';
  const d = new Date(at);
  return Number.isNaN(d.getTime()) ? String(at)
    : d.toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric',
                                  hour: '2-digit', minute: '2-digit' });
};

const testo = (v) => {
  const s = (v === 0 ? '0' : v || '').toString().trim();
  return s || '—';
};

const Riga = ({ etichetta, children, mono }) => (
  <>
    <div className="camp-k">{etichetta}</div>
    <div className={`camp-v${mono ? ' camp-mono' : ''}`}>{children}</div>
  </>
);

/** Elenco di numeri o email raccolti: pastiglie, o un trattino se non c'era niente. */
const Elenco = ({ voci }) => {
  const v = (voci || []).filter(Boolean);
  if (!v.length) return <span className="camp-niente">—</span>;
  return (
    <span className="camp-elenco">
      {v.map((x) => <span key={x} className="camp-tag">{x}</span>)}
    </span>
  );
};

/* L'esito del confronto fra la partita IVA della lista CERVED e quella letta sul sito: e' la
   prova che il sito (e quindi il numero) e' davvero di quell'azienda. */
const ESITO_PIVA = {
  combacia: { segno: '✓', testo: 'combacia con la lista', classe: 'ok' },
  diversa: { segno: '✗', testo: 'diversa: il sito è di un’altra azienda', classe: 'err' },
  non_trovata: { segno: '?', testo: 'non trovata sul sito', classe: 'warn' },
};

/** La sezione «Dati CERVED»: i gruppi ordinati di campagneData, le coordinate col link. */
function DatiCerved({ cerved }) {
  const gruppi = gruppiCerved(cerved);
  const coord = coordinateCerved(cerved);
  if (!gruppi.length && !coord) return null;
  return (
    <div className="camp-sez">
      <div className="camp-sez-tit">Dati CERVED</div>
      {gruppi.map((g) => (
        <div key={g.titolo} className="camp-cerved-gruppo">
          <div className="camp-cerved-tit">{g.titolo}</div>
          <div className="camp-griglia">
            {g.voci.map((v) => (
              <Riga key={v.k} etichetta={v.k}>
                {v.forte ? <span className="camp-forte">{v.v}</span>
                  : v.titolo ? <span className="camp-aiuto" title={v.titolo}>{v.v}</span> : v.v}
              </Riga>
            ))}
          </div>
        </div>
      ))}
      {coord && (
        <div className="camp-griglia">
          <Riga etichetta="Coordinate" mono>
            <span className="camp-sito-riga">
              {coord.testo}
              <a className="btn btn-secondary camp-btn-mini" href={coord.url}
                target="_blank" rel="noopener noreferrer">Apri su Google Maps</a>
            </span>
          </Riga>
        </div>
      )}
    </div>
  );
}

export default function CampagnaRigaDialog({ rigaId: rigaIniziale, onClose, onCambiata }) {
  /* La riga mostrata puo' essere un'altra da quella aperta dalla tabella: il riquadro
     «Già vista in…» apre la riga della lista precedente NELLO STESSO dialogo. `indietro`
     tiene le righe da cui si e' arrivati, per tornarci. Aprendo dalla tabella si riparte. */
  // Allineato DURANTE il render (non in un effetto): con un effetto, riaprendo il dialogo su
  // un'altra riga si vedeva per un fotogramma la riga aperta la volta prima.
  const [nav, setNav] = useState({ da: rigaIniziale, id: rigaIniziale, indietro: [] });
  if (nav.da !== rigaIniziale) setNav({ da: rigaIniziale, id: rigaIniziale, indietro: [] });
  const rigaId = nav.da === rigaIniziale ? nav.id : rigaIniziale;
  const indietro = nav.da === rigaIniziale ? nav.indietro : [];
  const [riga, setRiga] = useState(null);
  const [stato, setStato] = useState('carico');   // 'carico' | 'ok' | 'errore'
  const [errore, setErrore] = useState('');
  const [conferma, setConferma] = useState(false);
  // 'numeri_ambigui': il fisso scelto da chi guarda, che va mandato al backend
  const [scelto, setScelto] = useState('');
  const [busy, setBusy] = useState(false);

  const leggi = useCallback(async () => {
    const r = await axios.get(`${API}/campagne/riga/${rigaId}`);
    return r.data;
  }, [rigaId]);

  // Cambiando riga si riparte da zero: `vivo` scarta la risposta di una riga che nel
  // frattempo non e' piu' quella aperta.
  useEffect(() => {
    if (!rigaId) return undefined;
    let vivo = true;
    setStato('carico'); setRiga(null); setErrore(''); setConferma(false); setScelto('');
    leggi()
      .then((d) => { if (vivo) { setRiga(d); setStato('ok'); } })
      .catch((e) => {
        if (!vivo) return;
        setErrore(e.response?.status === 404
          ? 'Questa riga non esiste più.'
          : 'Non sono riuscito a leggere la riga.');
        setStato('errore');
      });
    return () => { vivo = false; };
  }, [rigaId, leggi]);

  if (!rigaIniziale || !rigaId) return null;

  const chiudi = () => { if (!busy) onClose?.(); };

  const apriRiga = (id) => {
    if (!id || id === rigaId || busy) return;
    setNav((n) => ({ ...n, id, indietro: [...n.indietro, n.id] }));
  };
  const torna = () => {
    if (busy || !indietro.length) return;
    setNav((n) => ({ ...n, id: n.indietro[n.indietro.length - 1], indietro: n.indietro.slice(0, -1) }));
  };

  /* Riarmo della sola riga aperta: stessa regola del pulsante di massa (/righe/riarma), che
     il backend restringe comunque ai motivi riarmabili. "Zero modificate" vuol dire che nel
     frattempo la riga e' cambiata: si rilegge e si dice com'e' adesso. */
  const riarma = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/campagne/righe/riarma`, { ids: [rigaId], attiva: true });
      const vera = await leggi().catch(() => null);
      if (vera) setRiga(vera);
      if (r.data?.modificate) {
        toast.success('Riga riarmata: torna da indagare, con l’indagine accesa.');
        onCambiata?.(vera);
      } else {
        toast.info(`Niente da riarmare: la riga adesso è «${etichettaStato(vera?.stato)}».`);
      }
    } catch {
      toast.error('Non sono riuscito a riarmare la riga');
    }
    setBusy(false);
  };

  /* Promozione a mano: la decisione e' di chi guarda la riga, non del motore. Il messaggio
     del backend (400) si mostra com'e' — dice PERCHE' quella riga non si puo' promuovere,
     e un "errore" generico al suo posto sarebbe un passo indietro. */
  const promuovi = async (numero) => {
    setBusy(true);
    try {
      // il corpo parte SOLO col numero scelto ('numeri_ambigui'): le altre promozioni
      // restano la chiamata di sempre, senza corpo
      const url = `${API}/campagne/riga/${rigaId}/promuovi`;
      const r = numero ? await axios.post(url, { numero }) : await axios.post(url);
      if (r.data?.gia_presente) {
        // il motivo lo decide il backend (il numero, oppure la partita IVA): affermare
        // sempre "quel numero era gia' in archivio" voleva dire dire il contrario di
        // quello che, un secondo dopo, si legge scritto sulla riga
        const perche = etichettaMotivo(r.data.motivo);
        toast.info(`Non è nato nessun record${perche ? `: ${perche}` : ''}. `
          + 'La riga è stata scartata e collegata al record che c’era già.');
      } else {
        toast.success('Numero messo in archivio: la riga è risolta.');
      }
      setConferma(false); setScelto('');
      const vera = await leggi().catch(() => null);
      if (vera) setRiga(vera);
      onCambiata?.(vera);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Non sono riuscito a mettere il numero in archivio');
    }
    setBusy(false);
  };

  const ident = riga ? etichettaIdentita(riga.identita) : null;
  const numero = riga ? numeroMascherato(riga) : '';
  const mapsQualcosa = riga && (riga.maps_ragione_sociale || riga.maps_indirizzo || riga.maps_url);
  const indagata = !!riga?.indagata_at;
  const chiaveArchivio = riga ? (riga.numero_trovato || riga.partita_iva || '') : '';
  const vista = riga ? giaVista(riga) : null;
  const fresca = !!riga && (!riga.id || riga.id === rigaId);
  const cerved = riga ? eCerved(riga) : false;
  const esitoPiva = riga ? ESITO_PIVA[confrontoPiva(riga)] : null;

  return createPortal(
    <div className="confirm-overlay" onClick={chiudi}>
      <div className="camp-dlg camp-dlg-largo" onClick={(e) => e.stopPropagation()}>
        <div className="camp-dlg-head">
          {indietro.length > 0 && (
            <button type="button" className="camp-azzera camp-indietro" onClick={torna} disabled={busy}>
              ← Torna alla riga di prima
            </button>
          )}
          <span className="camp-dlg-titolo camp-dlg-titolo-riga">
            {(fresca && riga.ragione_sociale) || 'Riga della campagna'}
          </span>
          <button type="button" className="camp-dlg-x" onClick={chiudi} aria-label="Chiudi">✕</button>
        </div>

        {/* `fresca`: la riga in memoria e' davvero quella da mostrare. Passando a un'altra riga
            (Apri quella riga, Torna) l'effetto di lettura parte DOPO il render, e senza questo
            controllo per un fotogramma si leggerebbe la riga di prima col titolo nuovo. */}
        {(stato === 'carico' || (stato === 'ok' && !fresca)) && (
          <div className="camp-dlg-body"><div className="camp-nota">Carico la riga…</div></div>
        )}
        {stato === 'errore' && (
          <div className="camp-dlg-body"><div className="camp-box camp-box-err">{errore}</div></div>
        )}

        {stato === 'ok' && fresca && (
          <div className="camp-dlg-body">
            {/* L'azienda era gia' passata da una lista precedente. Finche' la riga e'
                scartata per questo e' il segnale che spiega lo scarto; se e' stata riarmata
                (o indagata dopo) resta come storia — conta sempre lo stato attuale. */}
            {vista && (
              <div className={`camp-vista${vista.storia ? ' storia' : ''}`}>
                <span>
                  {vista.storia ? 'Era già stata vista in ' : 'Già vista in '}
                  <b>“{vista.campagna || 'una campagna senza nome'}”</b>
                  {vista.data ? <> il <b>{vista.data}</b></> : null}
                  {vista.esito ? <> — esito: <b>{vista.esito}</b></> : null}
                </span>
                {vista.rigaId && (
                  <button type="button" className="btn btn-secondary camp-btn-mini"
                    disabled={busy} onClick={() => apriRiga(vista.rigaId)}>
                    Apri quella riga
                  </button>
                )}
              </div>
            )}

            {/* ── 1. Cosa ha dato la lista (TIM, o CERVED) ── */}
            <div className="camp-sez">
              <div className="camp-sez-tit">
                {cerved ? <>Cosa ha dato la lista <span className="camp-tipo">CERVED</span></> : 'Cosa ha dato TIM'}
              </div>
              <div className="camp-griglia">
                <Riga etichetta="Ragione sociale">{testo(riga.ragione_sociale)}</Riga>
                {cerved ? (
                  <Riga etichetta="Numero">
                    <span className="camp-niente">—</span>
                    <span className="camp-nota-inline">
                      la lista CERVED non ha il numero: lo cerca l’indagine sul sito
                    </span>
                  </Riga>
                ) : (
                  <Riga etichetta="Numero mascherato" mono>
                    {testo(numero)}
                    {riga.linea_contatto && riga.linea_contatto !== numero && (
                      <span className="camp-nota-inline">nel file: {riga.linea_contatto}</span>
                    )}
                  </Riga>
                )}
                <Riga etichetta="Partita IVA" mono>{testo(riga.partita_iva)}</Riga>
                <Riga etichetta="Codice fiscale" mono>{testo(riga.codice_fiscale)}</Riga>
                <Riga etichetta="Indirizzo">
                  {testo(riga.indirizzo)}
                  {(riga.comune || riga.provincia) && (
                    <span className="camp-nota-inline">
                      {riga.comune || ''}{riga.provincia ? ` (${riga.provincia})` : ''}
                    </span>
                  )}
                </Riga>
                {/* email, tipo impresa e consenso le liste CERVED non li portano: tre
                    trattini in fila direbbero solo "manca qualcosa" */}
                {!cerved && <Riga etichetta="Email">{testo(riga.email)}</Riga>}
                {!cerved && <Riga etichetta="Tipo impresa">{testo(riga.tipo_impresa)}</Riga>}
                {/* consenso e codice opposizione vivono SOLO qui: il record d'archivio che
                    nasce dalla riga ci rimanda, e sono il motivo per cui una campagna con
                    righe risolte non si puo' cancellare */}
                {!cerved && <Riga etichetta="Consenso">{testo(riga.consenso_i1)}</Riga>}
                <Riga etichetta="Codice opposizione" mono>{testo(riga.codice_opposizione)}</Riga>
              </div>
            </div>

            {cerved && <DatiCerved cerved={riga.cerved} />}

            {/* ── 2. Cosa ha trovato l'indagine — la scheda di Maps per prima ── */}
            <div className="camp-sez">
              <div className="camp-sez-tit">Cosa ha trovato l’indagine</div>

              {mapsQualcosa ? (
                <div className="camp-maps">
                  <div className="camp-maps-tit">La scheda aperta su Google Maps</div>
                  <div className="camp-maps-rs">{testo(riga.maps_ragione_sociale)}</div>
                  <div className="camp-maps-ind">{testo(riga.maps_indirizzo)}</div>
                  <div className="camp-maps-piede">
                    {riga.maps_url && (
                      <a className="btn btn-secondary camp-btn-mini" href={riga.maps_url}
                        target="_blank" rel="noopener noreferrer">Apri la scheda su Maps</a>
                    )}
                    {ident && <span className="camp-ident-lbl" title={ident.titolo}>{ident.label}</span>}
                  </div>
                </div>
              ) : (
                <div className="camp-box camp-box-warn">
                  {indagata
                    ? 'Nessuna scheda di Maps: la ricerca non è arrivata su nessuna azienda.'
                    : 'Questa riga non è ancora stata indagata.'}
                </div>
              )}

              <div className="camp-griglia">
                <Riga etichetta="Sito">
                  {riga.sito ? (
                    <span className="camp-sito-riga">
                      <span className="camp-sito-url" title={riga.sito}>{riga.sito}</span>
                      <a className="btn btn-secondary camp-btn-mini" href={riga.sito}
                        target="_blank" rel="noopener noreferrer">Apri il sito</a>
                    </span>
                  ) : <span className="camp-niente">—</span>}
                </Riga>
                {/* La prova delle liste CERVED: la partita IVA letta sul sito, confrontata
                    con quella (certa) della lista. */}
                {cerved && (
                  <Riga etichetta="P.IVA sul sito" mono>
                    {riga.piva_sito ? riga.piva_sito : <span className="camp-niente">—</span>}
                    {esitoPiva && (
                      <span className={`camp-esito ${esitoPiva.classe}`}>
                        <b aria-hidden="true">{esitoPiva.segno}</b> {esitoPiva.testo}
                      </span>
                    )}
                  </Riga>
                )}
                {cerved && (
                  <Riga etichetta="«Tel.» sul sito" mono>{testo(riga.telefono_sito)}</Riga>
                )}
                <Riga etichetta="Numero su Maps" mono>{testo(riga.telefono_maps)}</Riga>
                <Riga etichetta="Fissi dal sito" mono><Elenco voci={riga.fissi} /></Riga>
                <Riga etichetta="Cellulari" mono><Elenco voci={riga.cellulari} /></Riga>
                <Riga etichetta="Email trovate"><Elenco voci={riga.email_trovate} /></Riga>
                <Riga etichetta="Indagata">
                  {indagata ? fmtData(riga.indagata_at) : 'mai'}
                  {riga.tentativi ? (
                    <span className="camp-nota-inline">
                      {riga.tentativi} tentativ{riga.tentativi === 1 ? 'o' : 'i'}
                    </span>
                  ) : null}
                </Riga>
              </div>
            </div>

            {/* ── 3. Il verdetto ── */}
            <div className="camp-sez">
              <div className="camp-sez-tit">Il verdetto</div>
              <div className="camp-griglia">
                <Riga etichetta="Stato">
                  <span className="camp-pill" style={{ '--pill': coloreStato(riga.stato) }}
                    title={titoloStato(riga.stato, riga.motivo)}>
                    {etichettaStato(riga.stato)}
                  </span>
                </Riga>
                <Riga etichetta="Motivo">{testo(etichettaMotivo(riga.motivo))}</Riga>
                <Riga etichetta="Numero trovato" mono>
                  {riga.numero_trovato
                    ? <span className="camp-trovato">{riga.numero_trovato}</span>
                    : <span className="camp-niente">—</span>}
                </Riga>
                {/* Il collegamento cerca il record per numero (o, se il numero non c'e',
                    per partita IVA): l'elenco numeri apre gia' filtrato su quello. Senza
                    nulla da cercare si dice che il record c'e' e basta — meglio un dato in
                    meno di un collegamento che promette di aprirlo e non lo apre. */}
                <Riga etichetta="Record in archivio">
                  {!riga.record_id ? <span className="camp-niente">nessuno</span>
                    : chiaveArchivio ? (
                      <Link className="camp-link" to={`/numeri?q=${encodeURIComponent(chiaveArchivio)}`}
                        onClick={() => onClose?.()}>
                        Apri il record in archivio
                      </Link>
                    ) : <span>c’è, ma senza numero né partita IVA per ritrovarlo</span>}
                </Riga>
              </div>
            </div>

            {riarmabileRiga(riga) && (
              <button type="button" className="btn btn-secondary camp-promuovi"
                disabled={busy} onClick={riarma}
                title="Lo scarto era una scelta, non un fatto: la riga torna da indagare, con l’indagine accesa">
                {busy ? 'Riarmo…' : 'Riarma questa riga'}
              </button>
            )}

            {/* Il pulsante esiste SOLO dove ha senso: con 'nessun_match' o
                'candidati_ambigui' il numero trovato non e' quello della campagna, e
                scriverlo in archivio sarebbe il danno che il sottosistema evita. */}
            {/* 'numeri_ambigui' (CERVED): la partita IVA c'e', i fissi sono piu' d'uno e
                nessuno spicca. Si sceglie quale mettere in archivio, uno per volta, con
                conferma: il numero scelto parte nel corpo della promozione. */}
            {promuovibileRiga(riga) && serveSceltaNumero(riga) && (
              <div className="camp-scelta-fissi">
                <div className="camp-scelta-tit">
                  Partita IVA confermata sul sito, ma i numeri fissi sono più d’uno e nessuno
                  spicca: scegli tu quello da mettere in archivio.
                </div>
                {fissiDaScegliere(riga).map((f) => (
                  <div key={f} className={`camp-scelta-riga${scelto === f ? ' sel' : ''}`}>
                    <span className="camp-trovato">{f}</span>
                    {scelto !== f && (
                      <button type="button" className="btn btn-secondary camp-btn-mini"
                        disabled={busy} onClick={() => { setScelto(f); setConferma(true); }}>
                        Metti in archivio questo
                      </button>
                    )}
                  </div>
                ))}
                {conferma && scelto && (
                  <div className="camp-box camp-box-warn">
                    Sto per scrivere in archivio il numero <b>{scelto}</b> intestato a{' '}
                    <b>{riga.ragione_sociale}</b>
                    {riga.partita_iva ? <> (P.IVA {riga.partita_iva})</> : null}: nasce un record
                    nuovo e questa riga diventa <b>risolta</b>. Gli altri numeri trovati restano
                    solo qui. Se quel numero è già in archivio, la riga viene invece scartata e
                    collegata al record che c’è già.
                    <div className="camp-conferma-btns">
                      <button type="button" className="btn btn-secondary" disabled={busy}
                        onClick={() => { setConferma(false); setScelto(''); }}>Annulla</button>
                      <button type="button" className="btn btn-primary" disabled={busy}
                        onClick={() => promuovi(scelto)}>
                        {busy ? 'Scrivo…' : `Sì, metti in archivio ${scelto}`}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {promuovibileRiga(riga) && !serveSceltaNumero(riga) && (
              conferma ? (
                <div className="camp-box camp-box-warn">
                  {/* 'piva_non_confermata': l'azienda e' stata riconosciuta su Maps, ma la
                      prova delle liste CERVED (la partita IVA sul sito) manca. Va detto
                      prima di scrivere, non dopo. */}
                  {riga.motivo === 'piva_non_confermata' && (
                    <div className="camp-conferma-avviso">
                      Attenzione: la partita IVA <b>non</b> è stata verificata sul sito. Il numero
                      viene dalla scheda di Maps riconosciuta, ma niente prova che sia davvero
                      di questa azienda.
                    </div>
                  )}
                  Sto per scrivere in archivio il numero <b>{riga.numero_trovato}</b> intestato a{' '}
                  <b>{riga.ragione_sociale}</b>
                  {riga.partita_iva ? <> (P.IVA {riga.partita_iva})</> : null}: nasce un record
                  nuovo e questa riga diventa <b>risolta</b>. Se quel numero è già in archivio,
                  la riga viene invece scartata e collegata al record che c’è già.
                  <div className="camp-conferma-btns">
                    <button type="button" className="btn btn-secondary" disabled={busy}
                      onClick={() => setConferma(false)}>Annulla</button>
                    <button type="button" className="btn btn-primary" disabled={busy}
                      onClick={() => promuovi()}>
                      {busy ? 'Scrivo…'
                        : riga.motivo === 'piva_non_confermata'
                          ? 'Sì, metti in archivio senza verifica' : 'Sì, metti in archivio'}
                    </button>
                  </div>
                </div>
              ) : (
                <button type="button" className="btn btn-primary camp-promuovi"
                  onClick={() => setConferma(true)}
                  title={riga.motivo === 'piva_non_confermata'
                    ? 'Il motore ha trovato il numero ma sul sito non ha trovato la partita IVA: la decisione è tua'
                    : 'Il motore ha trovato il numero ma non ha potuto verificare l’azienda: la decisione è tua'}>
                  Metti in archivio questo numero
                </button>
              )
            )}
          </div>
        )}

        <div className="camp-dlg-piede">
          <button type="button" className="btn btn-secondary" onClick={chiudi} disabled={busy}>Chiudi</button>
        </div>
      </div>
    </div>,
    document.body
  );
}
